import { Injectable, OnDestroy } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { DeleteModalDialogComponent } from "../components/dialog/delete-modal-dialog.component";
import { ServiceModeModalDialogComponent } from "../components/dialog/service-mode-modal-dialog.component";
import { UnsavedModalDialogComponent, UnsavedResultState } from "../components/unsaved-modal-dialog/unsaved-modal-dialog.component";
import { UploadProtocolModalDialogComponent } from "../components/upload/protocol-modal-dialog.component";
import { UploadSetupModalDialogComponent } from "../components/upload/upload-modal-dialog.component";
import { DatafoxDevice } from "../models/datafox-device.model";
import { Utilities } from "../models/utilities.model";
import { AlertService, MessageSeverity } from "./alert.service";
import { AuthService } from "./auth.service";
import { TerminalsEndpointService } from "./terminals-endpoint.service";

@Injectable()
export class TerminalsService implements OnDestroy {

  public allTerminalsChanged: BehaviorSubject<DatafoxDevice[]> = new BehaviorSubject<DatafoxDevice[]>(null);
  protected selectedTempModel: DatafoxDevice = null;
  public editedModelChanged: BehaviorSubject<DatafoxDevice> = new BehaviorSubject<DatafoxDevice>(null);
  protected oldModel: DatafoxDevice;
  protected nextModel: DatafoxDevice;
  public terminalsSearchList: Array<DatafoxDevice> = new Array<DatafoxDevice>();

  public _model: BehaviorSubject<DatafoxDevice> = new BehaviorSubject<DatafoxDevice>(null);
  isPersonSavedChanged: BehaviorSubject<DatafoxDevice> = new BehaviorSubject<DatafoxDevice>(null);

  public allDeviceTypes: string[];

  private logoutSubscription: Subscription;

  constructor(private terminalsEndpointService: TerminalsEndpointService, public dialog: MatDialog,
    private router: Router, private route: ActivatedRoute, private alertService: AlertService, private authService: AuthService) {

    this.logoutSubscription = this.authService.logoutEmitter.subscribe(logout => {
      if (logout == false) {
        this.updateTerminals();
        this.updateDeviceTypes();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
    }
  }

  public updateDeviceTypes() {
    this.getDeviceTypes().then(types => {
      this.allDeviceTypes = types;
    }).catch(error => {

    });
  }

  public updateTerminals() {
    this.getAllDataFoxDevices().then(terminals => {
      this.allTerminalsChanged.next(terminals);
    }).catch(error => { });
  }

  public uploadUpdate() {
    const dialog = this.dialog.open(UploadSetupModalDialogComponent, {
      data: {}, width: '30vw'
    });
    dialog.afterClosed().subscribe(result => {
      if (dialog.componentInstance.accept) {

      }
    });
  }

  public openUpdateProtocol() {
    const dialog = this.dialog.open(UploadProtocolModalDialogComponent, {
      data: {}
    });
    dialog.afterClosed().subscribe(result => {
      if (dialog.componentInstance.accept) {

      }
    });
  }

  public get allTerminals(): DatafoxDevice[] {
    return this.allTerminalsChanged.value;
  }

  public getAllDataFoxDevices(): Promise<DatafoxDevice[]> {
    return new Promise((resolve, reject) => {
      this.terminalsEndpointService.getAllDataFoxDevices(false).subscribe({
        next: devices => {
          resolve(devices);
        },
        error: error => {
          this.alertService.showMessage('Serverfehler', 'Beim Abfragen der Datafox Terminals ist ein Fehler aufgetreten!', MessageSeverity.error);
          reject();
        }
      });
    });
  }

  public getDeviceTypes(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      this.terminalsEndpointService.getDeviceTypes(false).subscribe({
        next: types => {
          resolve(types);
        },
        error: error => {
          this.alertService.showMessage('Serverfehler', 'Beim Laden der Device Typen ist ein Fehler aufgetreten!', MessageSeverity.error);
          reject();
        }
      });
    });
  }

  public set model(value: DatafoxDevice) {
    this._model.next(value);
    //console.log('vocational school changed', value);

    if (value && !value.serialNumber && !this.isEditMode) {
      this.startEditMode();
    }
  }

  public get model(): DatafoxDevice {
    return this._model.getValue();
  }

  public getSelectedModel() {
    return this.model;
  }

  public get editedModel(): DatafoxDevice {
    return this.editedModelChanged.value;
  }

  public set editedModel(value: DatafoxDevice) {
    if (value != this.editedModel) {
      this.editedModelChanged.next(value);
    }
  }

  public getOldModel() {
    return this.oldModel;
  }

  public isModelSelected() {
    return (this.model != null);
  }

  getEditedModel(): DatafoxDevice {
    return this.editedModel;
  }

  public getTerminalById(id: string, type: string): Observable<DatafoxDevice> {
    // console.log("Fetch terminal with serialnumber " + id);
    if (id == "new") {
      if (this.getSelectedModel() && this.getSelectedModel().serialNumber) {
        this.selectedTempModel = this.getSelectedModel();
      }
      let newEmployee: DatafoxDevice = this.generateNewTerminal();
      return Observable.create(observer => {
        observer.next(newEmployee);
        observer.complete();
      });
    }

    return this.terminalsEndpointService.get(id, type);
  }

  private generateNewTerminal(): DatafoxDevice {
    let newDatafoxDevice: DatafoxDevice = new DatafoxDevice();

    return newDatafoxDevice;
  }

  // add new employee
  public addNewTerminal() {
    //console.log("add new vocational school model");
    this.router.navigate(['/terminals/new/new']);
  }

  startEditMode() {
    this.startEditModeBase(() => this.setUserForEditMode(this.getSelectedModel()), this.getSelectedModel());
  }

  setUserForEditMode(user: DatafoxDevice) {
    this.editedModel = Utilities.deepClone(user); // copy by value
    this.oldModel = Utilities.deepClone(user);
  }

  public stopEditMode() {
    // add new terminal and cancel the process ==> select old selected terminal
    var selectedUser = this.getSelectedModel();
    if (selectedUser && !selectedUser.serialNumber && this.selectedTempModel) {
      if (this.selectedTempModel) {
        let serialnumber: string = this.selectedTempModel.serialNumber;
        let deviceType: string = this.selectedTempModel.deviceType;
        this.selectedTempModel = null;
        this.router.navigate(['/terminals/' + deviceType + '/' + serialnumber]);
      }
    } else if (!this.selectedTempModel && (!selectedUser?.serialNumber || !selectedUser?.deviceType)) {
      this.router.navigate(['/terminals/']);
    }

    this.isEditMode = false;
    this.isEditModeChanged.next(this.isEditMode);
    this.showErrors = false;
  }

  public discardChanges() {
    this.editedModel = Utilities.deepClone(this.oldModel);// copy by value
    this.isdiscardChanged.next(true);
    this.showErrors = false;
  }

  public isListVisible: boolean = true;

  public isTerminalLinkDisabled() {
    return this.isBackdropOkay() && this.isModelChanged();
  }

  public isModelSaveable(): boolean {
    return (this.isEditMode && this.isModelLinkDisabled() && this.isFormularValid);
  }

  isModelChanged() {
    // 1. stringify 2. compare only values (not types) of objects
    // P0D ist die gleiche Duration Schreibweise wie PT0S 
    // P0D erzeugt der Client und PT0S der Server => bei einer Dauer von 0 ergibt es dadurch immer eine Änderung
    // Workaround => Ersetze P0D mit PT0S (oder umgekehrt), da gleiche Bedeutung
    if (this.oldModel) {
      let old = JSON.stringify(this.oldModel, this.replaceEmptyWithNull).replace('P0D', 'PT0S');
      let edited = JSON.stringify(this.editedModel, this.replaceEmptyWithNull).replace('P0D', 'PT0S');
      return old !== edited;
    } else {
      return false;
    }
  }

  isModelLinkDisabled() {
    return this.isBackdropOkay() && this.isModelChanged();
  }

  public isModelDelete: boolean = false;

  public saveModel(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.oldModel.serialNumber && this.oldModel.deviceType) {
        this.saveTerminal(this.editedModel).then(res => {
          this.setModelForEditMode(res, true);
          this.updateTerminals();
          this.updateDeviceTypes();
          resolve(true);
        }).catch(error => {
          resolve(false);
        });
      } else {
        // create
        this.createTerminal(this.editedModel).then(res => {
          this.setModelForEditMode(res, true);
          this.updateTerminals();
          this.gotoTerminal(res);
          this.updateDeviceTypes();
          resolve(true);
        }).catch(error => {
          resolve(false);
        });
      }
    });
  }

  private saveTerminal(terminal: DatafoxDevice): Promise<DatafoxDevice> {
    return new Promise((resolve, reject) => {
      this.terminalsEndpointService.update(terminal, false).subscribe({
        next: data => {
          resolve(data);
        },
        error: error => {
          this.alertService.showMessage('Serverfehler', 'Beim speichern des Terminals ist ein Fehler aufgetreten!', MessageSeverity.error);
          reject();
        }
      });
    });
  }

  private createTerminal(terminal: DatafoxDevice): Promise<DatafoxDevice> {
    return new Promise((resolve, reject) => {
      this.terminalsEndpointService.create(terminal).subscribe({
        next: data => {
          resolve(data);
        },
        error: error => {
          this.alertService.showMessage('Serverfehler', 'Beim Anlegen des Terminals ist ein Fehler aufgetreten!', MessageSeverity.error);
          reject();
        }
      });
    });
  }

  setModelForEditMode(model: any, complete: boolean = false) {
    if (complete) {
      this._model.next(model);
    }

    this.editedModel = Utilities.deepClone(model); // copy by value
    this.oldModel = Utilities.deepClone(model);
  }


  public selectUserFromList(terminal: DatafoxDevice, e) {
    if (terminal) {
      e.stopPropagation();
      e.preventDefault();

      this.gotoTerminal(terminal);
    }
  }

  public selectTerminal(terminal: DatafoxDevice) {
    this.setTerminal(terminal);
  }

  public setTerminal(terminal: DatafoxDevice) {
    if (terminal) {
      this.setUserForEditMode(terminal);
    }
    this.model = terminal;
  }

  private gotoTerminal(terminal: DatafoxDevice) {
    // navigating manually to user profile because we disabled routerLink in memberlist
    // and we also want to keep the active Tab
    // console.log(" ** Navigating to terminal/" + terminal.serialNumber);
    this.router.navigate(["/terminals/", terminal.deviceType, terminal.serialNumber], { relativeTo: this.route });
  }

  public deleteModelQuestion() {
    var dialog = this.getDeleteQuestionDialog('Soll das ausgewählte Terminal wirklich gelöscht werden?');

    dialog.afterClosed().subscribe(result => {
      if (dialog.componentInstance.accept) {
        this.deleteUser();
      }
    });
  }

  public getDeleteQuestionDialog(text: string) {
    const dialogRef = this.dialog.open(DeleteModalDialogComponent, {
      data: { text: text }, width: '30vw'
    });
    return dialogRef;
  }

  // delete selected terminal
  public deleteUser() {
    this.terminalsEndpointService.delete(this.getSelectedModel().serialNumber, this.getSelectedModel().deviceType, false).subscribe({
      next: result => {
        this.alertService.showMessage('', 'Das ausgewählte Terminal wurde gelöscht!', MessageSeverity.success);
        // route to /employees/list
        this.selectTerminal(null);
        this.updateTerminals();
        this.updateDeviceTypes();
        this.router.navigate(['/terminals']);
      },
      error: error => {
        this.alertService.showMessage('Serverfehler', 'Beim Löschen ist ein Fehler aufgetreten', MessageSeverity.error);
      }
    });
  }

  public unsavedQuestionDialog(cancelButton: boolean = false, discardButton: boolean = true, isSaveable: boolean = true) {
    const dialogRef: MatDialogRef<UnsavedModalDialogComponent, UnsavedResultState> = this.dialog.open(UnsavedModalDialogComponent, {
      data: { showCancelButton: cancelButton, showDiscardButton: discardButton, isSaveable: isSaveable }
    });
    return dialogRef;
  }

  public cancelDeleteModel() {
    this.isModelDelete = false;
  }

  public serviceModeQuestionDialog(cancelButton: boolean = false, discardButton: boolean = true, isSaveable: boolean = true) {
    const dialogRef: MatDialogRef<ServiceModeModalDialogComponent, UnsavedResultState> = this.dialog.open(ServiceModeModalDialogComponent, {
      data: { showCancelButton: cancelButton, showDiscardButton: discardButton, isSaveable: isSaveable }
    });
    return dialogRef;
  }

  public setServiceMode(device: DatafoxDevice): Promise<boolean>  {
    return new Promise((resolve, reject) => {
      var dialogRef = this.serviceModeQuestionDialog(true);

      dialogRef.afterClosed().subscribe(result => {
        if (dialogRef.componentInstance.accept) {
          this.terminalsEndpointService.setServiceMode(device, false).subscribe({
            next: res => {
              this.updateTerminals();
              resolve(true);
            },
            error: error => {
              this.alertService.showMessage('Serverfehler', 'Beim Aktivieren des Service Modus ist ein Fehler aufgetreten!', MessageSeverity.error);
              reject(error);
            }
          });
        } else {
          resolve(false);
        }
      });
    });
  }

  /* 
  Edit Mode Area
*/
  public isEditMode: boolean = false;
  isBackdropOkay() {
    return this.isEditMode;
  }

  // change event for isEditMode
  isEditModeChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isListChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isSavedSuccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isdiscardChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  protected startEditModeBase(continuation: (obj: any) => void, obj: any) {
    this.isEditMode = true;
    continuation(obj);
    this.isEditModeChanged.next(this.isEditMode);
  }

  // Workaround to deal with empty and null values
  replaceEmptyWithNull(key, value) {
    return (value == "") ? null : value
  }

  /*
  is whole formular valid? then employee can be saved.
*/

  private formsArray: Formular[] = new Array();

  public addFormToValidation(newForm: NgForm, name: string) {
    let form: Formular = { form: newForm, name: name, valid: newForm.valid, useOwnValid: false };
    let search = this.formsArray.find(x => x.name == form.name);
    if (search) {
      let index: number = this.formsArray.indexOf(search);
      if (index > -1) {
        this.formsArray.splice(index, 1);
      }
    }
    this.formsArray.push(form);
    //console.log(this.formsArray);
    //console.log(name + " registered");

    //for (let form of this.formsArray) {
    //    console.log("form: " + form.name);
    //    console.log("is valid: " + form.form.valid);
    //}
  }

  public updateFormValidation(name: string, valid: boolean) {
    let search = this.formsArray.find(x => x.name == name);
    if (search) {
      let index: number = this.formsArray.indexOf(search);
      if (index > -1) {
        this.formsArray[index].valid = valid;
        this.formsArray[index].useOwnValid = true;
      }
    }
  }

  public removeFormValidation(name: string) {
    let search = this.formsArray.find(x => x.name == name);
    if (search) {
      let index: number = this.formsArray.indexOf(search);
      if (index > -1) {
        this.formsArray.splice(index, 1);
      }
    }
  }

  private isFormInvalid(element: Formular, index: number, array: Formular[]) {
    if (element.form) {
      return (element.useOwnValid) ? !element.valid : element.form.invalid;
    }
    return false;
  }

  public get isFormularValid(): boolean {
    return !this.formsArray.some(this.isFormInvalid);
  }

  public formularErrors: string;
  public showErrors: boolean;
  public showFormularError() {
    this.showErrors = true;
  }

}

export interface Formular {
  form: NgForm;
  name: string;
  valid: boolean;
  useOwnValid: boolean;
}
